import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import DataApi from '../../api/DataApi';
import EmailApi from '../../api/EmailApi';
import { formatDateTime } from '../../utils/formatUtils';

const FileSelector = ({
  show,
  onHide,
  recordId,
  selectedAttachments = [], // Array of {id, file_name} objects
  onSelectionChange,
  title = 'Select Files',
  allowPublicFiles = false, // For future use
}) => {
  console.log('FileSelector: Record ID:', recordId, show);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState(selectedAttachments || []);

  useEffect(() => {
    if (show && recordId) {
      console.log('FileSelector: Modal opened with recordId:', recordId);
      fetchAttachments();
    } else if (show && !recordId) {
      console.warn('FileSelector: Modal opened but no recordId provided');
      setError('Record ID is required to fetch files');
    }
  }, [show, recordId]);

  useEffect(() => {
    // Sync selectedItems with prop when it changes
    setSelectedItems(selectedAttachments || []);
  }, [selectedAttachments]);

  const fetchAttachments = async () => {
    if (!recordId) {
      setAttachments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('FileSelector: Fetching attachments for recordId:', recordId);

      // Use the attachments API endpoint: /api/attachments/:objectId
      const emailApi = new EmailApi();
      const attachmentsResponse = await emailApi.getAttachments(recordId);

      console.log('FileSelector: API Response:', attachmentsResponse);

      if (!attachmentsResponse?.success) {
        throw new Error(attachmentsResponse?.message || 'Failed to fetch attachments');
      }

      let records = [];

      // Handle different response structures
      if (Array.isArray(attachmentsResponse.data)) {
        records = attachmentsResponse.data;
      } else if (attachmentsResponse.data && Array.isArray(attachmentsResponse.data.data)) {
        records = attachmentsResponse.data.data;
      } else if (attachmentsResponse.data) {
        // If data is a single object, wrap it in an array
        records = [attachmentsResponse.data];
      }

      console.log('FileSelector: Records received:', records);
      console.log('FileSelector: Number of records:', records.length);

      // Transform records to attachment format
      console.log('FileSelector: Raw records before transformation:', JSON.stringify(records, null, 2));

      const transformedAttachments = records.map((record, index) => {
        console.log(`FileSelector: Processing record ${index}:`, record);

        // Helper function to extract value from nested structure
        const getValue = (fieldName) => {
          // Handle nested object structure with value property
          if (record[fieldName] !== undefined && record[fieldName] !== null) {
            if (typeof record[fieldName] === 'object' && !Array.isArray(record[fieldName])) {
              // Check if it has a value property (nested structure)
              if (record[fieldName].value !== undefined) {
                return record[fieldName].value;
              }
              // If it's an object but no value property, return the object itself
              return record[fieldName];
            }
            // Direct value
            return record[fieldName];
          }

          // Try alternative field names
          const alternatives = {
            'file_name': ['name', 'title'],
            'file_type': ['filetype', 'type'],
            'file_size': ['filesize', 'size'],
            'file_url': ['fileurl', 'url'],
            'parent_id': ['parent_id'],
          };

          if (alternatives[fieldName]) {
            for (const alt of alternatives[fieldName]) {
              if (record[alt] !== undefined && record[alt] !== null) {
                if (typeof record[alt] === 'object' && record[alt].value !== undefined) {
                  return record[alt].value;
                }
                return record[alt];
              }
            }
          }

          return null;
        };

        // Extract values, handling both nested and flat structures
        const id = getValue('id');
        const file_name = getValue('file_name') || 'Unknown';
        const file_type = getValue('file_type') || '';
        const file_size = getValue('file_size');
        const description = getValue('description') || '';
        const createddate = getValue('createddate') || '';
        const file_url = getValue('file_url') || '';

        console.log(`FileSelector: Extracted values for record ${index}:`, {
          id,
          file_name,
          file_type,
          file_size,
          description,
          createddate,
          file_url
        });

        // Convert file_size to number if it's a string
        let fileSizeNum = 0;
        if (file_size) {
          if (typeof file_size === 'string') {
            fileSizeNum = parseInt(file_size, 10) || 0;
          } else if (typeof file_size === 'number') {
            fileSizeNum = file_size;
          }
        }

        const attachment = {
          id: id,
          file_name: file_name,
          file_type: file_type,
          file_size: fileSizeNum,
          description: description,
          createddate: createddate,
          file_url: file_url,
        };

        console.log('FileSelector: Transformed attachment:', JSON.stringify(attachment, null, 2));
        console.log('FileSelector: Attachment id:', id);
        console.log('FileSelector: Attachment file_name:', file_name);

        // Validate that we have at least an id
        if (!id) {
          console.error('FileSelector: Warning - attachment missing id:', record);
        }

        return attachment;
      });

      console.log('FileSelector: Transformed attachments:', JSON.stringify(transformedAttachments, null, 2));
      console.log('FileSelector: Number of attachments:', transformedAttachments.length);

      // Filter out any attachments without an id
      const validAttachments = transformedAttachments.filter(att => att && att.id);
      console.log('FileSelector: Valid attachments (with id):', validAttachments.length);

      if (validAttachments.length === 0) {
        console.warn('FileSelector: No valid attachments found after transformation');
        setError('No valid attachments found. Please check the data structure.');
      }

      setAttachments(validAttachments);

    } catch (error) {
      console.error('FileSelector: Error fetching attachments:', error);
      console.error('FileSelector: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError(error.response?.data?.message || error.message || 'An error occurred while fetching attachments');
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (attachment) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(item => item.id === attachment.id);
      let newSelection;

      if (isSelected) {
        // Remove from selection
        newSelection = prev.filter(item => item.id !== attachment.id);
      } else {
        // Add to selection with id and file_name
        newSelection = [...prev, {
          id: attachment.id,
          file_name: attachment.file_name
        }];
      }

      // Notify parent component with array of {id, file_name} objects
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    const allItems = attachments.map(att => ({
      id: att.id,
      file_name: att.file_name
    }));
    setSelectedItems(allItems);
    if (onSelectionChange) {
      onSelectionChange(allItems);
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatAttachmentDate = (dateString) => {
    if (!dateString) return '';
    return formatDateTime(dateString);
  };

  const getFileIcon = (fileType) => {
    const type = (fileType || '').toLowerCase();
    if (type.includes('pdf')) return 'fas fa-file-pdf text-danger';
    if (type.includes('word') || type.includes('doc')) return 'fas fa-file-word text-primary';
    if (type.includes('excel') || type.includes('xls')) return 'fas fa-file-excel text-success';
    if (type.includes('image') || ['jpg', 'jpeg', 'png', 'gif'].includes(type)) return 'fas fa-file-image text-info';
    if (type.includes('zip') || type.includes('rar')) return 'fas fa-file-archive text-warning';
    return 'fas fa-file text-secondary';
  };

  // Debug render state
  useEffect(() => {
    console.log('FileSelector: State update - show:', show, 'attachments:', attachments.length, 'loading:', loading, 'error:', error);
  }, [show, attachments.length, loading, error]);

  if (!show) {
    console.log('FileSelector: Not rendering because show is false');
    return null;
  }

  // Render modal in a portal to avoid z-index issues with parent modals
  const modalContent = (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop={true}
      keyboard={true}
      style={{ zIndex: 1060 }}
      className="file-selector-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-file me-2"></i>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {console.log('FileSelector: Modal has been opened:')}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" className="me-2" />
            <span>Loading files...</span>
          </div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-folder-open fa-3x mb-3"></i>
            <p>No files found for this record.</p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <span className="text-muted">
                  {attachments.length} file{attachments.length !== 1 ? 's' : ''} found
                </span>
                {selectedItems.length > 0 && (
                  <span className="ms-2 text-primary">
                    ({selectedItems.length} selected)
                  </span>
                )}
              </div>
              <div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleSelectAll}
                  className="me-2"
                >
                  Select All
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleDeselectAll}
                >
                  Deselect All
                </Button>
              </div>
            </div>

            <div
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #ced4da',
                borderRadius: '0.375rem',
                padding: '10px',
                backgroundColor: '#fff',
              }}
            >
              {attachments.map((attachment) => {
                const isSelected = selectedItems.some(item => item.id === attachment.id);
                return (
                  <div
                    key={attachment.id}
                    style={{
                      padding: '10px',
                      marginBottom: '8px',
                      border: isSelected
                        ? '2px solid #0d6efd'
                        : '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: isSelected
                        ? '#f0f7ff'
                        : '#fff',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleToggle(attachment)}
                  >
                    <div className="d-flex align-items-center">
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(attachment)}
                        onClick={(e) => e.stopPropagation()}
                        className="me-3"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <i className={`${getFileIcon(attachment.file_type)} me-2`} style={{ fontSize: '1.5rem' }}></i>
                          <div className="flex-grow-1">
                            <div className="fw-bold">{attachment.file_name}</div>
                            <div className="text-muted small">
                              {formatFileSize(attachment.file_size)}
                              {attachment.file_type && ` • ${attachment.file_type.toUpperCase()}`}
                              {attachment.createddate && ` • ${formatAttachmentDate(attachment.createddate)}`}
                            </div>
                            {attachment.description && (
                              <div className="text-muted small mt-1">
                                {attachment.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {allowPublicFiles && (
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  Public file selection will be available in a future update.
                </small>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={onHide}
          disabled={selectedItems.length === 0}
        >
          {selectedItems.length > 0
            ? `Confirm (${selectedItems.length} selected)`
            : 'Confirm'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // Use portal to render outside the parent modal's DOM hierarchy
  return createPortal(modalContent, document.body);
};

export default FileSelector;
